import { computePosition, flip, offset, shift } from '@floating-ui/dom';

const STORAGE_KEY = 'calendar-popover-anchor';

const OFFSET = 10;
const MARGIN = 12;

const readAnchor = () => {
  if (window.__calendarPopoverAnchor) return window.__calendarPopoverAnchor;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
};

const writeAnchor = anchor => {
  window.__calendarPopoverAnchor = anchor;

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
  } catch (_error) {
    // Ignore storage failures (private mode, quota, etc.)
  }
};

// LiveView fires click-away, then the click under the pointer. A slot click
// would close this menu and open it again on the new slot.
const dismissSlotMenu = (event, close) => {
  const menu = document.getElementById('slot-new-appointment');
  if (!menu || menu.contains(event.target)) return false;
  if (!event.target.closest('[phx-click="new_event_at"]')) return false;

  event.preventDefault();
  event.stopPropagation();
  close();
  return true;
};

const captureAnchorFromEvent = event => {
  const target = event.target.closest('[data-calendar-popover-anchor]');
  if (!target) return;

  // Viewport coords — popover is position:fixed.
  const rect = target.getBoundingClientRect();
  writeAnchor({
    rect: {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    },
    ts: Date.now(),
  });
};

const contentInset = () => {
  const main = document.querySelector('main');
  if (!main) return 0;

  return parseFloat(window.getComputedStyle(main).paddingLeft) || 0;
};

const virtualAnchor = rect => ({
  getBoundingClientRect: () => ({ ...rect, x: rect.left, y: rect.top }),
});

const positionPopover = el => {
  const rect = readAnchor()?.rect;

  // Always reveal — including the no-anchor fallback position — so the popover
  // never stays stuck invisible after being hidden for mount.
  if (!rect) {
    el.style.visibility = 'visible';
    return;
  }

  const padding = { top: MARGIN, right: MARGIN, bottom: MARGIN, left: contentInset() + MARGIN };

  computePosition(virtualAnchor(rect), el, {
    strategy: 'fixed',
    placement: 'right',
    middleware: [
      offset(OFFSET),
      // All-day events span the week, leaving no room on either side.
      flip({ fallbackPlacements: ['left', 'bottom-start', 'top-start'], padding }),
      shift({ padding, crossAxis: true }),
    ],
  }).then(({ x, y }) => {
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.right = 'auto';
    el.style.visibility = 'visible';
  });
};

const SCROLL_LOCK_CLASS = 'calendar-slot-menu-open';

const lockPageScroll = () => {
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);

  const onWheel = event => {
    const menu = document.getElementById('slot-new-appointment');
    if (menu && menu.contains(event.target)) return;

    event.preventDefault();
  };

  window.addEventListener('wheel', onWheel, { passive: false, capture: true });
  window.addEventListener('touchmove', onWheel, { passive: false, capture: true });

  return () => {
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    window.removeEventListener('wheel', onWheel, { capture: true });
    window.removeEventListener('touchmove', onWheel, { capture: true });
  };
};

export default {
  CalendarPopoverAnchor: {
    mounted() {
      this.handlePointerDown = event => captureAnchorFromEvent(event);
      this.handleClick = event => {
        if (dismissSlotMenu(event, () => this.pushEvent('close_new_appointment_slot', {}))) return;

        captureAnchorFromEvent(event);
      };

      this.el.addEventListener('pointerdown', this.handlePointerDown, true);
      this.el.addEventListener('click', this.handleClick, true);
    },
    destroyed() {
      this.el.removeEventListener('pointerdown', this.handlePointerDown, true);
      this.el.removeEventListener('click', this.handleClick, true);
    },
  },
  CalendarPopoverPosition: {
    mounted() {
      this.handleResize = () => positionPopover(this.el);

      // One frame lets the element lay out before we measure and reveal,
      // avoiding a flash at the CSS fallback position.
      this._raf = requestAnimationFrame(() => positionPopover(this.el));
      window.addEventListener('resize', this.handleResize);

      if (this.el.hasAttribute('data-slot-new-appointment')) {
        this.releaseScrollLock = lockPageScroll();
      }
    },
    updated() {
      // LiveView may re-apply the server style (visibility: hidden); re-position
      // and reveal synchronously so it doesn't paint hidden.
      positionPopover(this.el);
    },
    destroyed() {
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this.releaseScrollLock) this.releaseScrollLock();
      window.removeEventListener('resize', this.handleResize);
    },
  },
};
